import { getVenuePrimarySport } from 'constants/venues';
import type {
  GetEditAvailabilityQuery,
  GetTopCourtsByGeoQuery,
  VenueAccessTypesEnum,
  VenueNetsEnum,
} from 'types/generated/client';
import { LocationType, type VenueAddress } from 'utils/shared/coachBuilder';
import type { AddVenueResult } from 'components/forms/AddVenue/props';
import type { VenueAddressWithCity } from './types';

/** The venue shape both edit queries (game + availability) select. */
interface FetchedVenue {
  id: string;
  title: string;
  addressString: string;
  accessType?: VenueAccessTypesEnum | null;
  indoorCourtCount: number;
  outdoorCourtCount: number;
  pickleballNets?: VenueNetsEnum | null;
  latitude: number | null;
  longitude: number | null;
  timezone?: string | null;
  images: { url: string }[];
}

/**
 * Maps a venue fetched for the edit flow to the slider's `VenueAddress` shape, so
 * the LocationField/PreferredLocationsField can show the currently-selected venue
 * even when it isn't in the nearby geo results.
 */
export const fetchedVenueToVenueAddress = (venue: FetchedVenue): VenueAddress => ({
  id: venue.id,
  latitude: venue.latitude != null ? Number(venue.latitude) : null,
  longitude: venue.longitude != null ? Number(venue.longitude) : null,
  address: venue.addressString,
  images: venue.images.map((image) => image.url),
  indoorCourtCount: venue.indoorCourtCount,
  outdoorCourtCount: venue.outdoorCourtCount,
  title: venue.title,
  status: 'ACTIVE',
  locationType: LocationType.Venue,
  pickleballNets: venue.pickleballNets ?? null,
  accessType: venue.accessType ?? null,
  timezone: venue.timezone ?? null,
});

/** "Brooklyn, New York" for the preview pin lines. */
const toCityLabel = (city?: { name: string; countrySubdivision?: { name: string } } | null) =>
  city ? [city.name, city.countrySubdivision?.name].filter(Boolean).join(', ') : null;

/** The fields `toVenueAddress` reads — any venue row carrying them can be mapped. */
export type VenueAddressSource = Pick<
  AddVenueResult,
  'id' | 'addressString' | 'indoorCourtCount' | 'outdoorCourtCount' | 'title'
> & {
  latitude?: number | string | null;
  longitude?: number | string | null;
  images?: { url: string }[] | null;
  pickleballNets?: AddVenueResult['pickleballNets'] | null;
  accessType?: AddVenueResult['accessType'] | null;
  timezone?: string | null;
  city?: Parameters<typeof toCityLabel>[0];
} & Partial<Pick<AddVenueResult, 'hasPickleball' | 'hasPadel' | 'hasTennis'>>;

/** Maps a venue row (AddVenue modal pick, venue follow, …) to the slider's VenueAddress shape. */
export const toVenueAddress = (venue: VenueAddressSource): VenueAddressWithCity => ({
  id: venue.id,
  latitude: venue.latitude != null ? Number(venue.latitude) : null,
  longitude: venue.longitude != null ? Number(venue.longitude) : null,
  address: venue.addressString,
  images: venue.images?.map((image) => image.url) ?? null,
  indoorCourtCount: venue.indoorCourtCount,
  outdoorCourtCount: venue.outdoorCourtCount,
  title: venue.title,
  status: 'ACTIVE',
  locationType: LocationType.Venue,
  pickleballNets: venue.pickleballNets || null,
  accessType: venue.accessType || null,
  timezone: venue.timezone ?? null,
  cityLabel: toCityLabel(venue.city),
});

/** Maps a nearby-venues (geo query) row to the slider's VenueAddress shape. */
export const geoVenueToVenueAddress = (
  venue: GetTopCourtsByGeoQuery['venues'][0],
): VenueAddressWithCity => ({
  id: venue.id,
  latitude: venue.geometry?.coordinates?.[1] ?? null,
  longitude: venue.geometry?.coordinates?.[0] ?? null,
  address: venue.addressString,
  images: venue.images?.map((image) => image.url) ?? null,
  indoorCourtCount: venue.indoorCourtCount,
  outdoorCourtCount: venue.outdoorCourtCount,
  title: venue.title,
  status: 'ACTIVE',
  locationType: LocationType.Venue,
  pickleballNets: venue.pickleballNets || null,
  accessType: venue.accessType || null,
  timezone: venue.timezone ?? null,
  cityLabel: toCityLabel(venue.city),
  primarySport: getVenuePrimarySport(venue),
});

/** Form-value id for a typed-in address (no venues row); never written to the DB. */
const MANUAL_VENUE_ID_PREFIX = 'manual:';

export const isManualVenue = (venue: Pick<VenueAddress, 'locationType'> | null | undefined) =>
  venue?.locationType === LocationType.Custom;

/** A street address the host typed in, shaped like a venue so the slider and
 *  preview treat it as one. Persisted on the event's own address columns. */
export const manualAddressToVenueAddress = (
  address: string,
  latitude: number | null,
  longitude: number | null,
): VenueAddress => ({
  id: `${MANUAL_VENUE_ID_PREFIX}${latitude ?? 'x'},${longitude ?? 'x'}`,
  latitude,
  longitude,
  address,
  title: null,
  status: 'ACTIVE',
  locationType: LocationType.Custom,
  pickleballNets: null,
  timezone: null,
});

/** Form-value id for a saved custom court (a `user_custom_courts` row). */
const CUSTOM_COURT_ID_PREFIX = 'custom:';

export const customCourtVenueId = (customCourtId: string) =>
  `${CUSTOM_COURT_ID_PREFIX}${customCourtId}`;

/** The custom court id behind a form-value id, or null for a venue id. */
export const parseCustomCourtId = (formId: string): string | null =>
  formId.startsWith(CUSTOM_COURT_ID_PREFIX) ? formId.slice(CUSTOM_COURT_ID_PREFIX.length) : null;

/** The custom-court row both availability documents select (edit query + insert result). */
type FetchedCustomCourt = NonNullable<
  NonNullable<
    GetEditAvailabilityQuery['playerAvailabilitiesByPk']
  >['venues'][number]['userCustomCourt']
>;

/** A saved custom court shaped like a venue so the slider and preview treat it as one. */
export const customCourtToVenueAddress = (court: FetchedCustomCourt): VenueAddressWithCity => ({
  id: customCourtVenueId(court.id),
  latitude: court.latitude != null ? Number(court.latitude) : null,
  longitude: court.longitude != null ? Number(court.longitude) : null,
  address: court.fullAddress,
  title: court.title === court.fullAddress ? null : court.title,
  status: 'ACTIVE',
  locationType: LocationType.Custom,
  pickleballNets: null,
  timezone: null,
  cityLabel: null,
});
